import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  Bell,
  UserPlus,
  Heart,
  Star,
  MessageCircle,
  Check,
  X,
  ArrowLeft,
  CheckCheck,
  Award,
  Trophy,
  Beer
} from 'lucide-react';

function ActivityFeed() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    notifications,
    friendRequests,
    unreadNotificationCount,
    markNotificationRead,
    markAllNotificationsRead,
    acceptFriendRequest,
    declineFriendRequest,
    ratings,
    bars
  } = useApp();

  if (!user) {
    return (
      <div className="min-h-screen bg-dive-black py-6 px-4">
        <div className="max-w-2xl mx-auto text-center py-12">
          <Bell className="w-16 h-16 text-dive-gray mx-auto mb-4" />
          <h2 className="text-white text-xl mb-2">Sign In Required</h2>
          <p className="text-gray-400 text-sm mb-6">
            Sign in to view your notifications and activity
          </p>
          <Link to="/auth" className="btn-primary">
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  const handleAccept = async (requestId, e) => {
    e.stopPropagation();
    try {
      await acceptFriendRequest(requestId);
    } catch (err) {
      console.error('Error accepting request:', err);
    }
  };

  const handleDecline = async (requestId, e) => {
    e.stopPropagation();
    try {
      await declineFriendRequest(requestId);
    } catch (err) {
      console.error('Error declining request:', err);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read first
    if (!notification.is_read) {
      await markNotificationRead(notification.id);
    }
    
    // Navigate based on notification type
    if (notification.type === 'friend_request') {
      // Friend request - go to that user's profile
      navigate(`/social/${notification.from_user_id}`);
    } else if (notification.type === 'friend_accepted') {
      // Friend accepted - go to their profile
      navigate(`/social/${notification.from_user_id}`);
    } else if (notification.type === 'new_follower') {
      // Someone followed you - go to their profile
      navigate(`/social/${notification.from_user_id}`);
    } else if (notification.type === 'follow_request') {
      // Follow request - go to Social page requests
      navigate(`/social/${notification.from_user_id}`);
    } else if (notification.type === 'follow_request_accepted' || notification.type === 'follow_accepted') {
      // Follow accepted - go to their profile
      navigate(`/social/${notification.from_user_id}`);
    } else if (notification.type === 'rating_comment') {
      // Rating comment - go to My Feed tab in Friends to see the comment
      navigate('/social?tab=myfeed');
      return;
    } else if (notification.type === 'badge_earned') {
      // Badge earned - go to own profile to see badges
      navigate('/profile');
    } else if (notification.type === 'bar_request_approved') {
      // Navigate to the new bar page
      const barId = notification.data?.bar_id || notification.reference_id;
      if (barId) {
        navigate(`/bar/${barId}`);
      } else {
        navigate('/list');
      }
    } else if (notification.type === 'bar_request_rejected') {
      // Navigate to add bar page so they can try again
      navigate('/add');
    } else if (notification.type === 'check_in') {
      const barId = notification.data?.bar_id;
      if (barId) navigate(`/bar/${barId}`);
      else navigate('/social');
    }
  };

  const timeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;
    return date.toLocaleDateString();
  };

  const getNotificationDetails = (notification) => {
    if (notification.type === 'rating_comment') {
      const rating = ratings.find(r => r.id === notification.reference_id);
      if (rating) {
        const bar = bars.find(b => b.id === rating.barId);
        if (bar) {
          return { barName: bar.name, ratingId: rating.id };
        }
      }
    }
    return null;
  };

  const pendingRequests = friendRequests?.incoming || [];
  const allNotifications = notifications || [];

  return (
    <div className="min-h-screen bg-dive-black pb-24">
      {/* Header */}
      <div className="sticky z-40 bg-dive-black/95 backdrop-blur-sm border-b border-dive-gray" style={{ top: 'var(--app-top-offset)' }}>
        <div className="max-w-2xl mx-auto p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="p-2 text-gray-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="font-display text-xl text-white tracking-wide">
                  Activity
                </h1>
                <p className="text-gray-500 text-xs">
                  {unreadNotificationCount > 0 
                    ? `${unreadNotificationCount} unread`
                    : 'All caught up!'
                  }
                </p>
              </div>
            </div>
            
            {unreadNotificationCount > 0 && (
              <button
                onClick={() => markAllNotificationsRead()}
                className="flex items-center gap-2 px-3 py-2 text-sm text-dive-neon hover:bg-dive-neon/10 rounded-lg transition-colors"
              >
                <CheckCheck className="w-4 h-4" />
                Mark all read
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4">
        {/* Friend Requests Section */}
        {pendingRequests.length > 0 && (
          <div className="mb-6">
            <h2 className="text-white font-medium mb-3 flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-dive-neon" />
              Friend Requests
              <span className="bg-dive-amber text-white text-xs px-2 py-0.5 rounded-full">
                {pendingRequests.length}
              </span>
            </h2>
            <div className="space-y-2">
              {pendingRequests.map(request => (
                <div
                  key={request.id}
                  className="bg-dive-dark border border-dive-gray rounded-lg p-4 flex items-center gap-3"
                >
                  <div className="w-12 h-12 rounded-full bg-dive-neon/20 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {request.from_user?.avatar_url ? (
                      <img
                        src={request.from_user.avatar_url}
                        alt={request.from_user.display_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-dive-neon text-lg">
                        {(request.from_user?.display_name || 'U').charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-medium">
                      {request.from_user?.display_name || 'User'}
                    </p>
                    <p className="text-gray-500 text-sm">
                      @{request.from_user?.username} wants to be friends
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => handleDecline(request.id, e)}
                      className="px-3 py-2 bg-dive-gray text-gray-400 rounded-lg hover:text-red-400 transition-colors"
                    >
                      Decline
                    </button>
                    <button
                      onClick={(e) => handleAccept(request.id, e)}
                      className="px-3 py-2 bg-dive-neon text-white rounded-lg hover:bg-dive-neon/80 transition-colors"
                    >
                      Accept
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* All Notifications */}
        <div>
          <h2 className="text-white font-medium mb-3 flex items-center gap-2">
            <Bell className="w-5 h-5 text-gray-400" />
            Notifications
          </h2>

          {allNotifications.length === 0 ? (
            <div className="bg-dive-dark border border-dive-gray rounded-lg p-8 text-center">
              <Bell className="w-12 h-12 text-dive-gray mx-auto mb-3" />
              <p className="text-gray-400">No notifications yet</p>
              <p className="text-gray-600 text-sm mt-1">
                Notifications will appear here when friends interact with you
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {allNotifications.map(notification => {
                const details = getNotificationDetails(notification);
                
                return (
                  <button
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`w-full bg-dive-dark border rounded-lg p-4 flex items-start gap-3 text-left transition-all hover:border-dive-neon/50 ${
                      notification.is_read ? 'border-dive-gray' : 'border-dive-neon/30 bg-dive-neon/5'
                    }`}
                  >
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      notification.type === 'friend_accepted' ? 'bg-dive-neon/20' :
                      notification.type === 'new_follower' ? 'bg-dive-neon/20' :
                      notification.type === 'follow_request' ? 'bg-blue-500/20' :
                      notification.type === 'follow_request_accepted' || notification.type === 'follow_accepted' ? 'bg-dive-neon/20' :
                      notification.type === 'rating_comment' ? 'bg-amber-500/20' :
                      notification.type === 'badge_earned' ? 'bg-gradient-to-br from-amber-500/30 to-yellow-500/30' :
                      notification.type === 'bar_request_approved' ? 'bg-green-500/20' :
                      notification.type === 'bar_request_rejected' ? 'bg-red-500/20' :
                      notification.type === 'check_in' ? 'bg-amber-500/20' :
                      'bg-dive-gray'
                    }`}>
                      {notification.type === 'friend_accepted' ? (
                        <UserPlus className="w-5 h-5 text-dive-neon" />
                      ) : notification.type === 'new_follower' ? (
                        <UserPlus className="w-5 h-5 text-dive-neon" />
                      ) : notification.type === 'follow_request' ? (
                        <UserPlus className="w-5 h-5 text-blue-400" />
                      ) : notification.type === 'follow_request_accepted' || notification.type === 'follow_accepted' ? (
                        <UserPlus className="w-5 h-5 text-dive-neon" />
                      ) : notification.type === 'rating_comment' ? (
                        <MessageCircle className="w-5 h-5 text-amber-400" />
                      ) : notification.type === 'friend_request' ? (
                        <UserPlus className="w-5 h-5 text-blue-400" />
                      ) : notification.type === 'badge_earned' ? (
                        <Trophy className="w-5 h-5 text-amber-400" />
                      ) : notification.type === 'bar_request_approved' ? (
                        <Check className="w-5 h-5 text-green-400" />
                      ) : notification.type === 'check_in' ? (
                        <Beer className="w-5 h-5 text-dive-amber" />
                      ) : notification.type === 'bar_request_rejected' ? (
                        <X className="w-5 h-5 text-red-400" />
                      ) : (
                        <Bell className="w-5 h-5 text-gray-400" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className="text-white">
                        {notification.type === 'friend_accepted' && (
                          <>
                            <span className="font-medium">{notification.from_user?.display_name}</span>
                            {' '}accepted your friend request
                          </>
                        )}
                        {notification.type === 'new_follower' && (
                          <>
                            <span className="font-medium">{notification.from_user?.display_name}</span>
                            {' '}started following you
                          </>
                        )}
                        {notification.type === 'follow_request' && (
                          <>
                            <span className="font-medium">{notification.from_user?.display_name}</span>
                            {' '}requested to follow you
                          </>
                        )}
                        {(notification.type === 'follow_request_accepted' || notification.type === 'follow_accepted') && (
                          <>
                            <span className="font-medium">{notification.from_user?.display_name}</span>
                            {' '}accepted your follow request
                          </>
                        )}
                        {notification.type === 'rating_comment' && (
                          <>
                            <span className="font-medium">{notification.from_user?.display_name}</span>
                            {' '}commented on your rating
                            {details?.barName && (
                              <span className="text-dive-neon"> at {details.barName}</span>
                            )}
                          </>
                        )}
                        {notification.type === 'friend_request' && (
                          <>
                            <span className="font-medium">{notification.from_user?.display_name}</span>
                            {' '}sent you a friend request
                          </>
                        )}
                        {notification.type === 'bar_request_approved' && (
                          <>
                            🎉 Your bar request "<span className="font-medium">{notification.data?.bar_name || 'Unknown'}</span>" was approved! Rate it now →
                          </>
                        )}
                        {notification.type === 'bar_request_rejected' && (
                          <>
                            Your bar request "<span className="font-medium">{notification.data?.bar_name || 'Unknown'}</span>" was not approved
                          </>
                        )}
                        {notification.type === 'check_in' && (
                          <>
                            {notification.from_user?.display_name || 'A friend'} checked in: <span className="font-medium text-amber-400">{notification.data?.message_text || notification.data?.bar_name || 'a bar'}</span>
                          </>
                        )}
                        {notification.type === 'badge_earned' && (
                          <span className="flex items-center gap-1">
                            🏆 You earned the <span className="font-medium text-amber-400">{notification.data?.badge_name || 'Unknown'}</span> badge!
                          </span>
                        )}
                      </p>
                      <p className="text-gray-500 text-sm mt-1">
                        {timeAgo(notification.created_at)}
                      </p>
                    </div>

                    {/* Unread indicator */}
                    {!notification.is_read && (
                      <div className="w-2 h-2 bg-dive-neon rounded-full flex-shrink-0 mt-2" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ActivityFeed;

